/* Biometric Time-Punch dashboard — iter53.
 * Route: /biometric-punch
 * Two panels:
 *   1. Enroll new fingerprints (HR only) — pick an employee, tap "Enrôler"
 *      → browser prompts fingerprint/OS biometric → credential stored.
 *   2. Terminal — anyone can tap "Pointer" — the browser prompts biometric,
 *      the punch (in/out auto-detected) is recorded.
 *   3. Journal — HR view of today's punches.
 * Also lists enrolled credentials per employee + manual override for HR.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { biometricApi, teachersApi, staffApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Fingerprint, ClockCounterClockwise, Trash, UserCircle, ArrowClockwise, ArrowsClockwise, CurrencyDollar, WarningCircle, DownloadSimple, ArrowSquareOut } from '@phosphor-icons/react';
import { PlanningDialog } from '../components/biometric/PlanningDialog';
import { BiometricEnrollTab } from '../components/biometric/BiometricEnrollTab';
import { BiometricPayrollTab } from '../components/biometric/BiometricPayrollTab';
import { BiometricAlertsTab } from '../components/biometric/BiometricAlertsTab';
import { BiometricHistoryTab } from '../components/biometric/BiometricHistoryTab';

/* ---------- Base64 URL helpers (WebAuthn) ---------- */
const b64uToBytes = (str) => {
  const s = str.replace(/-/g, '+').replace(/_/g, '/');
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const bin = atob(s + pad);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
  return arr.buffer;
};
const bytesToB64u = (buf) => {
  const bin = String.fromCharCode(...new Uint8Array(buf));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const credentialForServer = (cred) => {
  const raw = cred.response;
  const payload = {
    id: cred.id,
    rawId: bytesToB64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bytesToB64u(raw.clientDataJSON),
    },
    clientExtensionResults: cred.getClientExtensionResults?.() || {},
  };
  if (raw.attestationObject) payload.response.attestationObject = bytesToB64u(raw.attestationObject);
  if (raw.authenticatorData) payload.response.authenticatorData = bytesToB64u(raw.authenticatorData);
  if (raw.signature) payload.response.signature = bytesToB64u(raw.signature);
  if (raw.userHandle) payload.response.userHandle = bytesToB64u(raw.userHandle);
  return payload;
};

const optionsToCreate = (opts) => ({
  ...opts,
  challenge: b64uToBytes(opts.challenge),
  user: { ...opts.user, id: b64uToBytes(opts.user.id) },
  excludeCredentials: (opts.excludeCredentials || []).map((c) => ({
    ...c, id: b64uToBytes(c.id),
  })),
});

const optionsToGet = (opts) => ({
  ...opts,
  challenge: b64uToBytes(opts.challenge),
  allowCredentials: (opts.allowCredentials || []).map((c) => ({
    ...c, id: b64uToBytes(c.id),
  })),
});

export default function BiometricPunch() {
  const [tab, setTab] = useState('terminal');
  const [teachers, setTeachers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [enrollKind, setEnrollKind] = useState('teacher');
  const [enrollId, setEnrollId] = useState('');
  const [enrollLabel, setEnrollLabel] = useState('Empreinte principale');
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState([]);
  const [punches, setPunches] = useState([]);
  const [today] = useState(() => new Date().toISOString().slice(0, 10));
  const [webauthnSupported, setWebauthnSupported] = useState(true);
  // iter54 — Payroll + HR alerts
  const [workHours, setWorkHours] = useState([]);
  const [workHoursFrom, setWorkHoursFrom] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [workHoursTo, setWorkHoursTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [hrAlerts, setHrAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  // iter55 — Planning editor dialog
  const [planningOpen, setPlanningOpen] = useState(false);
  const [planning, setPlanning] = useState({ start_time: '08:00', end_time: '17:00', work_days: [0, 1, 2, 3, 4], late_grace_minutes: 15 });

  useEffect(() => {
    setWebauthnSupported(!!window.PublicKeyCredential);
  }, []);

  const loadEmployees = useCallback(async () => {
    try {
      const [tr, sr] = await Promise.all([teachersApi.getAll(), staffApi.getAll()]);
      setTeachers(tr.data || []);
      setStaff(sr.data || []);
    } catch (e) {
      toast.error('Erreur chargement des employés');
    }
  }, []);

  const loadPunches = useCallback(async () => {
    try {
      const { data } = await biometricApi.listPunches({ date: today, limit: 200 });
      setPunches(data.items || []);
    } catch (e) {
      setPunches([]);
    }
  }, [today]);

  useEffect(() => { loadEmployees(); loadPunches(); }, [loadEmployees, loadPunches]);

  const loadWorkHours = useCallback(async () => {
    setPayrollLoading(true);
    try {
      const { data } = await biometricApi.workHours({ fromDate: workHoursFrom, toDate: workHoursTo });
      setWorkHours(data.items || []);
    } catch (e) { setWorkHours([]); toast.error(e.response?.data?.detail || 'Erreur paie'); }
    finally { setPayrollLoading(false); }
  }, [workHoursFrom, workHoursTo]);

  const loadAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const { data } = await biometricApi.hrAlerts();
      setHrAlerts(data.items || []);
    } catch (e) { setHrAlerts([]); }
    finally { setAlertsLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === 'payroll') loadWorkHours();
    if (tab === 'alerts') loadAlerts();
  }, [tab, loadWorkHours, loadAlerts]);

  const exportPayrollCsv = () => {
    const header = ['Employé', 'Catégorie', 'Fonction', 'Jours attendus', 'Heures attendues', 'Heures régulières', 'Heures sup.', 'Taux', 'Salaire', 'Paie estimée'];
    const rows = workHours.map((w) => [
      w.employee_name, w.staff_type, w.function || '',
      w.expected_days, w.expected_hours, w.hours_regular ?? w.hours_worked, w.hours_overtime ?? 0,
      w.expected_hours ? Math.round(((w.hours_regular || 0) + (w.hours_overtime || 0)) / w.expected_hours * 100) + '%' : '—',
      w.salary, w.estimated_pay,
    ]);
    const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `paie_${workHoursFrom}_${workHoursTo}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success('Rapport paie exporté');
  };

  const downloadPayslip = async (kind, employeeId, employeeName) => {
    try {
      const res = await biometricApi.payslipPdf(kind, employeeId, { fromDate: workHoursFrom, toDate: workHoursTo });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const safe = (employeeName || 'employe').replace(/\s+/g, '_');
      const a = document.createElement('a');
      a.href = url; a.download = `bulletin-paie-${safe}-${workHoursFrom}-${workHoursTo}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success('Bulletin téléchargé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur téléchargement bulletin');
    }
  };

  const openPlanning = async () => {
    if (!enrollId) { toast.error("Sélectionnez un employé d'abord"); return; }
    try {
      const { data } = await biometricApi.getPlanning(enrollKind, enrollId);
      setPlanning({
        start_time: data.planning.start_time || '08:00',
        end_time: data.planning.end_time || '17:00',
        work_days: data.planning.work_days || [0, 1, 2, 3, 4],
        late_grace_minutes: data.planning.late_grace_minutes || 15,
      });
      setPlanningOpen(true);
    } catch (e) { toast.error("Erreur chargement du planning"); }
  };

  const savePlanning = async () => {
    try {
      await biometricApi.setPlanning({
        kind: enrollKind, employee_id: enrollId,
        start_time: planning.start_time, end_time: planning.end_time,
        work_days: planning.work_days,
        late_grace_minutes: Number(planning.late_grace_minutes) || 15,
      });
      toast.success('Planning enregistré');
      setPlanningOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur enregistrement'); }
  };

  const sendReminders = async () => {
    try {
      const { data } = await biometricApi.sendArrivalReminders();
      toast.success(`${data.sent} rappel(s) envoyé(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // iter56 — Bulk PDF export + Bank export
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bankBusy, setBankBusy] = useState(false);
  const [notifyOnBulk, setNotifyOnBulk] = useState(true);  // iter57 — checkbox default ON
  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };
  const generateBulk = async (format) => {
    setBulkBusy(true);
    try {
      const res = await biometricApi.generatePayslipsBulk({
        from_date: workHoursFrom, to_date: workHoursTo, format,
        notify: notifyOnBulk,
      });
      const count = res.headers?.['x-payslip-count'] || '?';
      const notifSent = res.headers?.['x-notify-sent'] || '0';
      const ext = format === 'zip' ? 'zip' : 'pdf';
      const mime = format === 'zip' ? 'application/zip' : 'application/pdf';
      downloadBlob(new Blob([res.data], { type: mime }),
        `bulletins-paie-${workHoursFrom}-${workHoursTo}.${ext}`);
      const notifyMsg = notifyOnBulk ? ` — ${notifSent} employé(s) notifié(s)` : '';
      toast.success(`${count} bulletin(s) généré(s)${notifyMsg}`);
      if (tab === 'history') loadPayslipsHistory();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur génération groupée';
      toast.error(msg);
    } finally { setBulkBusy(false); }
  };
  const exportBank = async (format) => {
    setBankBusy(true);
    try {
      const res = await biometricApi.bankExportCsv({
        fromDate: workHoursFrom, toDate: workHoursTo, format,
      });
      const count = res.headers?.['x-bank-export-count'] || '?';
      downloadBlob(new Blob([res.data], { type: 'text/csv;charset=utf-8' }),
        `virements-${format}-${workHoursFrom}-${workHoursTo}.csv`);
      toast.success(`Export bancaire (${format.toUpperCase()}) — ${count} virement(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur export bancaire');
    } finally { setBankBusy(false); }
  };

  // iter58 — Reminder campaign
  const [campaignStats, setCampaignStats] = useState(null);
  const [campaignMonth, setCampaignMonth] = useState(() =>
    new Date().toISOString().slice(0, 7));  // YYYY-MM
  const [reminderBusy, setReminderBusy] = useState(false);
  const loadCampaignStats = useCallback(async () => {
    try {
      const { data } = await biometricApi.payslipCampaignStats({ month: campaignMonth });
      setCampaignStats(data);
    } catch (e) {
      setCampaignStats(null);
    }
  }, [campaignMonth]);
  useEffect(() => {
    if (tab === 'history') loadCampaignStats();
  }, [tab, loadCampaignStats]);
  const runReminderCampaign = async () => {
    setReminderBusy(true);
    try {
      const { data } = await biometricApi.triggerPayslipReminders();
      const summary = Object.entries(data.by_offset || {})
        .map(([n, c]) => `J+${n}: ${c}`).join(', ');
      toast.success(`Campagne exécutée — ${data.sent} relance(s) envoyée(s) (${summary || '0'})`);
      loadCampaignStats();
      if (tab === 'history') loadPayslipsHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur campagne');
    } finally { setReminderBusy(false); }
  };

  // iter60 — Compliance report
  const [complianceBusy, setComplianceBusy] = useState(false);
  const openComplianceReport = () => {
    const url = biometricApi.complianceReportUrl(campaignMonth);
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const regenerateCompliance = async () => {
    setComplianceBusy(true);
    try {
      const { data } = await biometricApi.generateCompliance(campaignMonth);
      toast.success(`Rapport ${data.key} régénéré (${data.total} bulletin(s), ${data.signed} signé(s))`);
      openComplianceReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur génération');
    } finally { setComplianceBusy(false); }
  };

  // iter56 — Historique bulletins
  const [payslipsHistory, setPayslipsHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyEmployee, setHistoryEmployee] = useState('');
  const loadPayslipsHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = { limit: 200 };
      if (historyEmployee) params.employeeId = historyEmployee;
      const { data } = await biometricApi.listPayslips(params);
      setPayslipsHistory(data.items || []);
    } catch (e) {
      setPayslipsHistory([]);
      toast.error(e.response?.data?.detail || 'Erreur chargement historique');
    } finally { setHistoryLoading(false); }
  }, [historyEmployee]);
  useEffect(() => {
    if (tab === 'history') loadPayslipsHistory();
  }, [tab, loadPayslipsHistory]);
  const downloadHistoryPdf = async (payslipId, employeeName) => {
    try {
      const { data } = await biometricApi.payslipPdfById(payslipId);
      const safe = (employeeName || 'bulletin').replace(/\s+/g, '_');
      downloadBlob(new Blob([data], { type: 'application/pdf' }),
        `bulletin-paie-${safe}.pdf`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur téléchargement'); }
  };
  const copySignLink = async (payslipId) => {
    try {
      const { data } = await biometricApi.payslipMeta(payslipId);
      const url = data.sign_url || '';
      if (!url) { toast.error('Lien indisponible'); return; }
      await navigator.clipboard.writeText(url);
      toast.success('Lien de signature copié dans le presse-papiers');
    } catch (e) { toast.error('Erreur copie du lien'); }
  };
  // iter57 — Send the signature link by email + SMS to the employee
  const sendSignLink = async (payslipId) => {
    try {
      const { data } = await biometricApi.sendPayslipSignLink(payslipId, ['email', 'sms']);
      const bits = [];
      if (data.email_sent) bits.push('email');
      if (data.sms_sent) bits.push('SMS');
      if (bits.length === 0) {
        toast.error("Aucun canal disponible (email/téléphone manquants ou service non configuré)");
      } else {
        toast.success(`Lien envoyé par ${bits.join(' + ')}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur envoi du lien");
    }
  };

  const loadCreds = useCallback(async () => {
    if (!enrollId) { setCreds([]); return; }
    try {
      const { data } = await biometricApi.listCredentials(enrollKind, enrollId);
      setCreds(data.items || []);
    } catch (e) { setCreds([]); }
  }, [enrollKind, enrollId]);

  useEffect(() => { loadCreds(); }, [loadCreds]);

  const employeeOptions = useMemo(() => {
    if (enrollKind === 'teacher') return teachers.map((t) => ({ id: t.id, label: `${t.last_name} ${t.first_name} — ${t.subject || 'Enseignant'}` }));
    return staff.map((s) => ({ id: s.id, label: `${s.last_name} ${s.first_name} — ${s.staff_type || 'staff'} — ${s.function || ''}` }));
  }, [enrollKind, teachers, staff]);

  const enroll = async () => {
    if (!enrollId) { toast.error('Sélectionnez un employé'); return; }
    if (!webauthnSupported) { toast.error("Ce navigateur ne supporte pas WebAuthn"); return; }
    setBusy(true);
    try {
      const { data } = await biometricApi.registerChallenge(enrollKind, enrollId);
      const cred = await navigator.credentials.create({ publicKey: optionsToCreate(data.options) });
      if (!cred) throw new Error('Enrôlement annulé');
      await biometricApi.registerVerify(data.challenge_id, credentialForServer(cred), enrollLabel);
      toast.success('Empreinte enrôlée avec succès');
      loadCreds();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Échec de l'enrôlement";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const punch = async () => {
    if (!webauthnSupported) { toast.error("Ce navigateur ne supporte pas WebAuthn"); return; }
    setBusy(true);
    try {
      const { data } = await biometricApi.authChallenge();
      const assertion = await navigator.credentials.get({ publicKey: optionsToGet(data.options) });
      if (!assertion) throw new Error('Pointage annulé');
      const { data: res } = await biometricApi.authVerify(data.challenge_id, credentialForServer(assertion), 'auto');
      const p = res.punch;
      toast.success(`${p.type === 'in' ? '✓ Entrée' : '✓ Sortie'} — ${p.employee_name} · ${new Date(p.timestamp).toLocaleTimeString('fr-FR')}`);
      loadPunches();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Pointage échoué';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const manualPunch = async (kind, id, type) => {
    try {
      const { data } = await biometricApi.manualPunch(kind, id, type);
      toast.success(`Pointage manuel ${type === 'in' ? 'Entrée' : 'Sortie'} enregistré`);
      loadPunches();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const deleteCred = async (id) => {
    if (!window.confirm('Supprimer cette empreinte ?')) return;
    try {
      await biometricApi.deleteCredential(id);
      toast.success('Empreinte supprimée');
      loadCreds();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // iter85 — Prop bag for the 3 extracted tab components. Includes every
  // piece of state, form value and callback the tabs reference. Any prop
  // unused by a given component is simply ignored (destructuring default).
  const tabProps = {
    // Enroll
    enrollId, setEnrollId, enrollKind, setEnrollKind, enrollLabel, setEnrollLabel,
    creds, teachers, staff, employeeOptions,
    enroll, deleteCred, busy, openPlanning, webauthnSupported,
    // Payroll
    workHoursFrom, setWorkHoursFrom, workHoursTo, setWorkHoursTo, workHours,
    loadWorkHours, payrollLoading, notifyOnBulk, setNotifyOnBulk,
    generateBulk, bulkBusy, exportPayrollCsv, exportBank, bankBusy,
    // Alerts
    today, loadAlerts, sendReminders, alertsLoading, hrAlerts,
    // History / Campaign
    campaignMonth, setCampaignMonth, campaignStats, loadCampaignStats,
    runReminderCampaign, reminderBusy, openComplianceReport,
    regenerateCompliance, complianceBusy,
    historyEmployee, setHistoryEmployee, loadPayslipsHistory,
    historyLoading, payslipsHistory, downloadHistoryPdf,
    sendSignLink, copySignLink,
    // Shared helpers used by extracted tabs
    manualPunch, downloadPayslip, toast,
  };

  return (
    <div className="p-6 space-y-4" data-testid="biometric-punch-page">
      <div>
        <h1 className="font-outfit text-2xl font-bold flex items-center gap-2">
          <Fingerprint size={26} className="text-[#0052FF]" weight="duotone" />
          Pointage biométrique du personnel
        </h1>
        <p className="text-sm text-[#64748B] mt-1">
          Enrôlement des empreintes, terminal de pointage et journal quotidien. Inclut la Direction.
        </p>
        {!webauthnSupported && (
          <div className="mt-2 bg-red-50 border border-red-200 text-red-700 p-2 rounded text-sm">
            Votre navigateur ne prend pas en charge WebAuthn (Fingerprint API). Utilisez Chrome/Edge/Firefox récent
            sur un appareil avec un capteur biométrique ou un lecteur USB CTAP2.
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="terminal" data-testid="tab-terminal">
            <Fingerprint size={16} className="mr-1.5" /> Terminal
          </TabsTrigger>
          <TabsTrigger value="enroll" data-testid="tab-enroll">
            <UserCircle size={16} className="mr-1.5" /> Enrôlement
          </TabsTrigger>
          <TabsTrigger value="journal" data-testid="tab-journal">
            <ClockCounterClockwise size={16} className="mr-1.5" /> Journal
          </TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll">
            <CurrencyDollar size={16} className="mr-1.5" /> Rapport paie
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            <WarningCircle size={16} className="mr-1.5" /> Alertes RH
            {hrAlerts.length > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[9px] font-bold">
                {hrAlerts.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <ClockCounterClockwise size={16} className="mr-1.5" /> Historique
          </TabsTrigger>
        </TabsList>

        {/* -------- Terminal -------- */}
        <TabsContent value="terminal" className="space-y-4">
          <div className="flex justify-end">
            <a
              href="/kiosk"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-[#0052FF] hover:underline font-semibold"
              data-testid="open-kiosk-link"
            >
              <ArrowSquareOut size={14} weight="bold" />
              Ouvrir en mode Kiosque (plein écran)
            </a>
          </div>
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-8 flex flex-col items-center gap-4">
              <button
                type="button"
                onClick={punch}
                disabled={busy || !webauthnSupported}
                className="w-40 h-40 rounded-full flex flex-col items-center justify-center bg-gradient-to-br from-[#0052FF] to-[#00A3FF] text-white shadow-xl hover:scale-105 transition disabled:opacity-50"
                data-testid="punch-btn"
              >
                <Fingerprint size={70} weight="duotone" />
                <span className="mt-1 text-sm font-semibold">{busy ? 'Attente…' : 'POINTER'}</span>
              </button>
              <p className="text-xs text-[#64748B]">
                Touchez ce bouton puis présentez votre doigt. L&apos;entrée / sortie est détectée automatiquement.
              </p>
            </CardContent>
          </Card>

          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-outfit font-semibold text-sm">Pointages d&apos;aujourd&apos;hui ({today})</h3>
                <Button variant="outline" size="sm" onClick={loadPunches} data-testid="refresh-punches-btn">
                  <ArrowClockwise size={14} className="mr-1" /> Rafraîchir
                </Button>
              </div>
              {punches.length === 0 ? (
                <p className="text-sm text-[#94A3B8] italic text-center py-3">Aucun pointage aujourd&apos;hui.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Heure</TableHead>
                      <TableHead>Employé</TableHead>
                      <TableHead>Catégorie</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Méthode</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {punches.map((p) => (
                      <TableRow key={p.id} data-testid={`punch-row-${p.id}`}>
                        <TableCell className="font-mono text-xs">
                          {new Date(p.timestamp).toLocaleTimeString('fr-FR')}
                        </TableCell>
                        <TableCell className="font-medium">{p.employee_name}</TableCell>
                        <TableCell>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                            {p.staff_type || p.kind}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                            p.type === 'in' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {p.type === 'in' ? 'ENTRÉE' : 'SORTIE'}
                          </span>
                        </TableCell>
                        <TableCell className="text-[10px] text-[#64748B]">
                          {p.method === 'biometric' ? '🔐 Empreinte' : '✍️ Manuel'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* -------- Enroll -------- */}
        <TabsContent value="enroll" className="space-y-4">
          <BiometricEnrollTab {...tabProps} />
        </TabsContent>

        {/* -------- Journal -------- */}
        <TabsContent value="journal" className="space-y-4">
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-outfit font-semibold">Journal — {today}</h3>
                <Button variant="outline" size="sm" onClick={loadPunches}>
                  <ArrowsClockwise size={14} className="mr-1" /> Rafraîchir
                </Button>
              </div>
              {punches.length === 0 ? (
                <p className="text-sm text-[#94A3B8] italic text-center py-8">Aucun pointage aujourd&apos;hui.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Heure</TableHead>
                      <TableHead>Employé</TableHead>
                      <TableHead>Fonction</TableHead>
                      <TableHead>Catégorie</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Méthode</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {punches.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-mono text-xs">{new Date(p.timestamp).toLocaleTimeString('fr-FR')}</TableCell>
                        <TableCell className="font-medium">{p.employee_name}</TableCell>
                        <TableCell className="text-xs">{p.function || '—'}</TableCell>
                        <TableCell>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                            {p.staff_type || p.kind}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                            p.type === 'in' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {p.type === 'in' ? 'ENTRÉE' : 'SORTIE'}
                          </span>
                        </TableCell>
                        <TableCell className="text-[10px]">{p.method === 'biometric' ? '🔐 Empreinte' : '✍️ Manuel'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* -------- Payroll -------- */}
        <TabsContent value="payroll" className="space-y-4">
          <BiometricPayrollTab {...tabProps} />
        </TabsContent>

        {/* -------- Alerts -------- */}
        <TabsContent value="alerts" className="space-y-4">
          <BiometricAlertsTab {...tabProps} />
        </TabsContent>

        {/* -------- History (iter56) -------- */}
        <TabsContent value="history" className="space-y-4">
          <BiometricHistoryTab {...tabProps} />
        </TabsContent>
      </Tabs>

      {/* iter55 — Planning editor dialog */}
      <PlanningDialog
        open={planningOpen}
        onOpenChange={setPlanningOpen}
        planning={planning}
        setPlanning={setPlanning}
        savePlanning={savePlanning}
      />
    </div>
  );
}
